// IR-1765 r2 (Rotem 66052) / IR-1805 r14 (Rotem 66790): the one place that words a group's Last Active cell.
//   - no reliable activity time -> "Unavailable" + an info icon; the tooltip says it is not evidence of inactivity;
//   - a date that may be outdated -> the date is kept + a subtle warning icon; the tooltip says why;
//   - otherwise the date alone.
// Neither explanation is persistent text. The backend flags (`last_active_unknown_reason`,
// `last_active_outdated_warning`) only choose the state; the wording lives here. A field the backend did not send
// (undefined) stays an em dash: it is absent, not unavailable.
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { AlertTriangle, Info } from "lucide-react";

export const LAST_ACTIVE_UNAVAILABLE_LABEL = "Unavailable";
/** Rotem 66790, verbatim. */
export const LAST_ACTIVE_UNAVAILABLE_TIP =
  "The latest activity time is unavailable. This does not mean the group is inactive.";
export const LAST_ACTIVE_OUTDATED_TIP =
  "Some activity information is missing. More recent activity may not be reflected in this date.";

const ICON_BUTTON_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  marginLeft: 6,
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "help",
} as const;

function formatLastActive(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * The plain-text Last Active of a group (detail panel, delete dialog): null/empty = Unavailable, undefined (the field
 * was not sent) = an em dash, otherwise the caller's date formatting.
 */
export function lastActiveText(
  iso: string | null | undefined,
  format: (iso: string) => string,
): string {
  if (iso === undefined) return "—";
  if (!iso) return LAST_ACTIVE_UNAVAILABLE_LABEL;
  return format(iso);
}

function IconTip({
  label,
  tip,
  color,
  children,
}: {
  label: string;
  tip: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label={label} style={{ ...ICON_BUTTON_STYLE, color }}>
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" style={{ maxWidth: 280 }}>
        {tip}
      </TooltipContent>
    </Tooltip>
  );
}

export function LastActiveDisplay({
  lastActiveAt,
  outdated,
}: {
  /** ISO activity time; null = Unavailable; undefined = the backend did not send the field (em dash). */
  lastActiveAt: string | null | undefined;
  /** True when a valid date may be outdated (incomplete processing). Never applies to Unavailable. */
  outdated?: boolean;
}) {
  if (lastActiveAt === undefined) return <>{"—"}</>;
  if (!lastActiveAt) {
    return (
      <span style={{ whiteSpace: "nowrap" }}>
        {LAST_ACTIVE_UNAVAILABLE_LABEL}
        <IconTip
          label="About Unavailable Last Active"
          tip={LAST_ACTIVE_UNAVAILABLE_TIP}
          color="var(--iris-fg-4)"
        >
          <Info size={12} aria-hidden />
        </IconTip>
      </span>
    );
  }
  return (
    <span style={{ whiteSpace: "nowrap" }}>
      {formatLastActive(lastActiveAt)}
      {outdated ? (
        <IconTip
          label="Last Active may be outdated"
          tip={LAST_ACTIVE_OUTDATED_TIP}
          color="var(--iris-neon-orange, var(--iris-fg-3))"
        >
          <AlertTriangle size={12} aria-hidden />
        </IconTip>
      ) : null}
    </span>
  );
}
